import React, { Component, createRef } from 'react';

import CheckBox from 'components/atoms/CheckBox';
import { ANALYIS_CHART_DATA } from 'data/static';
import { saveAs } from 'file-saver';
import Highcharts from 'highcharts';
import HighchartsReact, { HighchartsReactRefObject } from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import { ProfileDataType } from 'types';
import { translate } from 'utils/translate';
import { metersToNauticalMiles } from 'utils/unit';

interface ProfileChartProps {
    data: {
        label: string[];
        data: number[][];
        plotLines: [string, number][];
    };
    for: string;

    analysisData: ProfileDataType | null;
    transits: number[];
    chainage: { name: string }[];
    getHistogram: (index: number) => void;
    addExportFunction: (callback: () => void) => void;
}

interface ProfileChartState {
    options: any;
    chart: any;
    showHistogram: boolean;
    loading: boolean;
    histogram: Highcharts.SeriesOptionsType | null;
}

class ProfileChart extends Component<ProfileChartProps, ProfileChartState> {
    private chartRef = React.createRef<HighchartsReactRefObject>();

    constructor(props: ProfileChartProps) {
        super(props);
        this.chartRef = createRef();
        this.state = {
            options: null,
            chart: null,
            showHistogram: false,
            loading: false,
            histogram: null,
        };
    }

    attachExportButtonListener = (): void => {
        const exportButton = document.getElementById(`${this.props.for}ExportButton`);
        if (exportButton) {
            exportButton.addEventListener('click', this.exportToCSV);
        }
    };

    exportToCSV = () => {
        if (!this.props.transits) {
            return;
        }
        const { data, label } = this.props.data;

        const csvHeader = `${ANALYIS_CHART_DATA[this.props.for].yLabel} ${this.props.transits.length} transits along ${
            this.props.analysisData?.pilotagePlanAnalysis?.pilotagePlan?.route
        },,,,\nChainage (NM),chainage (m),min,5%,25%,Median,75%,95%,max\n`;

        const csvRows = data
            .map((point, i) => {
                return [
                    metersToNauticalMiles(label[i]),
                    label[i],
                    point[0] ? point[0].toFixed(1) : '',
                    point[1] ? point[1].toFixed(1) : '',
                    point[2] ? point[2].toFixed(1) : '',
                    point[3] ? point[3].toFixed(1) : '',
                    point[4] ? point[4].toFixed(1) : '',
                    point[5] ? point[5].toFixed(1) : '',
                    point[6] ? point[6].toFixed(1) : '',
                ].join(',');
            })
            .join('\n');

        const csvContent = csvHeader + csvRows;

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

        saveAs(
            blob,
            `TA profile result ${this.props.transits.length} transits - ${
                this.props.for
            } at chainage ${metersToNauticalMiles(
                this.props.analysisData?.pilotagePlanAnalysis.startChainage
            )} to ${metersToNauticalMiles(this.props.analysisData?.pilotagePlanAnalysis.endChainage)} along ${
                this.props.analysisData?.pilotagePlanAnalysis.pilotagePlan?.route
            }.csv`
        );
    };

    componentDidUpdate(): void {
        this.attachExportButtonListener();
    }

    componentDidMount(): void {
        this.props.addExportFunction(this.exportToCSV);
        this.attachExportButtonListener();
        HighchartsMore(Highcharts);

        this.createChart();
    }

    createChart() {
        const direction = ANALYIS_CHART_DATA[this.props.for].direction;
        this.setState({
            chart: this.props.data,
            options: {
                chart: {
                    type: 'boxplot',
                    events: {
                        render: function () {
                            if (this.customGroup) {
                                this.customGroup.destroy();
                            }

                            this.customGroup = this.renderer.g('custom-shapes').add();

                            function drawRectangles(chart) {
                                const yAxis = chart.yAxis[0];
                                const width = 10;

                                chart.renderer
                                    .rect(
                                        yAxis.left - width,
                                        yAxis.toPixels(yAxis.min),
                                        width,
                                        yAxis.toPixels(0) - yAxis.top
                                    )
                                    .attr({
                                        fill: 'red',
                                        opacity: 0.4,
                                    })
                                    .add(chart.customGroup);

                                chart.renderer
                                    .rect(
                                        yAxis.left - width,
                                        yAxis.toPixels(0),
                                        width,
                                        yAxis.toPixels(yAxis.max) - yAxis.toPixels(0)
                                    )

                                    .attr({
                                        fill: 'green',
                                        opacity: 0.4,
                                    })
                                    .add(chart.customGroup);
                            }
                            if (direction) {
                                drawRectangles(this);
                            }
                        },
                    },
                },

                title: {
                    text: `${translate(ANALYIS_CHART_DATA[this.props.for].title)} <button id="${
                        this.props.for
                    }ExportButton" alt=${translate('Download profile data')} title=${translate(
                        'Download profile data'
                    )} style=" float: right ;  margin-top: -7px; margin-left: 10px; font-size: 14px; color: blue;"><svg  class=" cursor-pointer w-8 h-8  " style="transform: scale(0.5)" viewBox="0 0 32 32" fill="none" stroke="currentcolor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"><path d="M28 22 L28 30 4 30 4 22 M16 4 L16 24 M8 16 L16 24 24 16"></path></svg></button>`,
                    useHTML: true,
                    style: { width: '60%', fontSize: '1rem', color: '#000000', fontWeight: '500' },
                },
                legend: {
                    enabled: false,
                },
                xAxis: {
                    categories: this.props.data.label.map((x) => {
                        return metersToNauticalMiles(parseInt(x));
                    }),
                    title: {
                        text:
                            this.props.chainage.length >= 1
                                ? translate(`Chainage from (NM)`, { nm: this.props.chainage[0]?.name })
                                : translate(`Chainage from start of route (NM)`),
                    },
                    labels: {
                        style: {
                            fontSize: '10px',
                        },
                    },
                    plotLines: Array.from(
                        new Map(this.props.data.plotLines.map((point) => [point[1], point])).values()
                    ).map((x) => {
                        return {
                            zIndex: 5,
                            value: x[1] + 0.1,
                            color: 'red',
                            width: 1,
                            label: {
                                text: x[0],
                                align: 'right',
                                verticalAlign: 'bottom',
                                y: -5,
                                style: {
                                    fontWeight: 'bold',
                                    color: 'red',
                                },
                            },
                        };
                    }),
                },
                additionalData: ANALYIS_CHART_DATA[this.props.for],
                yAxis: {
                    // tickInterval: ANALYIS_CHART_DATA[this.props.for].unit !== 'deg' ? null : 90,
                    // max: ANALYIS_CHART_DATA[this.props.for].unit !== 'deg' ? 360 : null,
                    endOnTick: false,
                    reversed: ANALYIS_CHART_DATA[this.props.for].reverse,
                    title: {
                        text: ANALYIS_CHART_DATA[this.props.for].yLabel,
                    },
                    labels: {
                        formatter: function () {
                            if (this.chart.options.additionalData.roundDeg) {
                                if (this.value < 0) {
                                    return ((this.value % 360) + 360) % 360;
                                }
                                return this.value;
                            } else if (this.chart.options.additionalData.direction) {
                                return `${this.value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} ${
                                    this.value == 0 ? '' : this.value > 0 ? 'S' : 'P'
                                }`;
                            }

                            return this.value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                        },
                        style: {
                            fontSize: '10px',
                        },
                    },
                    plotLines: ANALYIS_CHART_DATA[this.props.for].direction
                        ? [
                              {
                                  zIndex: 5,
                                  value: 0,
                                  color: 'black',
                                  width: 1,
                                  label: {
                                      text: '',
                                      align: 'right',
                                      verticalAlign: 'bottom',
                                      y: -5,
                                      style: {
                                          fontWeight: 'bold',
                                          color: 'black',
                                      },
                                  },
                              },
                          ]
                        : [],
                },
                credits: {
                    enabled: false,
                },
                tooltip: {
                    shared: true,
                    positioner: function (labelWidth, labelHeight, point) {
                        let tooltipX, tooltipY;

                        // Default position: tooltip to the right of the mouse pointer
                        tooltipX = point.plotX + this.chart.plotLeft + 10;

                        // If the tooltip would go outside the right boundary, position it to the left of the mouse pointer
                        if (tooltipX + labelWidth > this.chart.plotLeft + this.chart.plotWidth) {
                            tooltipX = point.plotX + this.chart.plotLeft - labelWidth - 10;
                        }

                        // If the tooltip would go outside the left boundary, position it to the right of the mouse pointer
                        if (tooltipX < this.chart.plotLeft) {
                            tooltipX = point.plotX + this.chart.plotLeft + 10;
                        }

                        // Center the tooltip vertically relative to the whisker box
                        tooltipY = point.plotY + this.chart.plotTop + point.h / 2 - labelHeight / 2;

                        // Prevent the tooltip from going outside the bottom boundary
                        if (tooltipY + labelHeight > this.chart.plotTop + this.chart.plotHeight) {
                            tooltipY = this.chart.plotTop + this.chart.plotHeight - labelHeight - 10;
                        }

                        // Prevent the tooltip from going outside the top boundary
                        if (tooltipY < this.chart.plotTop) {
                            tooltipY = this.chart.plotTop + 10;
                        }

                        return {
                            x: tooltipX,
                            y: tooltipY,
                        };
                    },
                    style: {
                        opacity: 0.7,
                        fontSize: '12px',
                        useHTML: true,
                    },
                },
                series: [
                    {
                        name: ANALYIS_CHART_DATA[this.props.for].yLabel,
                        lineWidth: 2,
                        zIndex: 1,
                        stemColor: '#36a2eb',
                        fillColor: 'rgba(126, 159 ,189, 0.5)',
                        data: this.props.data.data.map((x) => x.slice(1, 6)),
                        tooltip: {
                            zIndex: 6,
                            headerFormat: `<div class="font-bold	" >${translate('Chainage')} {point.key} (NM)</div>`,
                            pointFormatter: function () {
                                let point: any = {};
                                if (this.series.chart.options.additionalData.roundDeg) {
                                    point = {
                                        min: (
                                            (this.series.chart.options.series[1].data[this.index][0] + 360) %
                                            360
                                        ).toFixed(this.series.chart.options.additionalData.decimals),
                                        max: (
                                            (this.series.chart.options.series[1].data[this.index][1] + 360) %
                                            360
                                        ).toFixed(this.series.chart.options.additionalData.decimals),
                                        low: ((this.low + 360) % 360).toFixed(
                                            this.series.chart.options.additionalData.decimals
                                        ),
                                        high: ((this.high + 360) % 360).toFixed(
                                            this.series.chart.options.additionalData.decimals
                                        ),
                                        median: ((this.median + 360) % 360).toFixed(
                                            this.series.chart.options.additionalData.decimals
                                        ),
                                        q1: ((this.q1 + 360) % 360).toFixed(
                                            this.series.chart.options.additionalData.decimals
                                        ),
                                        q3: ((this.q3 + 360) % 360).toFixed(
                                            this.series.chart.options.additionalData.decimals
                                        ),
                                    };
                                } else if (this.series.chart.options.additionalData.direction) {
                                    point = {
                                        min: `${parseFloat(
                                            this.series.chart.options.series[1].data[this.index][0]
                                        ).toFixed(this.series.chart.options.additionalData.decimals)} ${
                                            this.series.chart.options.series[1].data[this.index][0].toFixed(
                                                this.series.chart.options.additionalData.decimals
                                            ) == 0
                                                ? ''
                                                : this.series.chart.options.series[1].data[this.index][0].toFixed(
                                                      this.series.chart.options.additionalData.decimals
                                                  ) > 0
                                                ? 'S'
                                                : 'P'
                                        }`,
                                        low: `${this.low.toFixed(this.series.chart.options.additionalData.decimals)} ${
                                            this.low.toFixed(this.series.chart.options.additionalData.decimals) == 0
                                                ? ''
                                                : this.low.toFixed(this.series.chart.options.additionalData.decimals) >
                                                  0
                                                ? 'S'
                                                : 'P'
                                        }`,
                                        high: `${parseFloat(this.high).toFixed(
                                            this.series.chart.options.additionalData.decimals
                                        )} ${
                                            this.high.toFixed(this.series.chart.options.additionalData.decimals) == 0
                                                ? ''
                                                : this.high.toFixed(this.series.chart.options.additionalData.decimals) >
                                                  0
                                                ? 'S'
                                                : 'P'
                                        }`,
                                        median: `${parseFloat(this.median).toFixed(
                                            this.series.chart.options.additionalData.decimals
                                        )} ${
                                            this.median.toFixed(this.series.chart.options.additionalData.decimals) == 0
                                                ? ''
                                                : this.median.toFixed(
                                                      this.series.chart.options.additionalData.decimals
                                                  ) > 0
                                                ? 'S'
                                                : 'P'
                                        }`,
                                        q1: `${parseFloat(this.q1).toFixed(
                                            this.series.chart.options.additionalData.decimals
                                        )} ${
                                            this.q1.toFixed(this.series.chart.options.additionalData.decimals) == 0
                                                ? ''
                                                : this.q1.toFixed(this.series.chart.options.additionalData.decimals) > 0
                                                ? 'S'
                                                : 'P'
                                        }`,
                                        q3: `${parseFloat(this.q3).toFixed(
                                            this.series.chart.options.additionalData.decimals
                                        )} ${
                                            this.q3.toFixed(this.series.chart.options.additionalData.decimals) == 0
                                                ? ''
                                                : this.q3.toFixed(this.series.chart.options.additionalData.decimals) > 0
                                                ? 'S'
                                                : 'P'
                                        }`,
                                        max: `${parseFloat(
                                            this.series.chart.options.series[1].data[this.index][1]
                                        ).toFixed(this.series.chart.options.additionalData.decimals)} ${
                                            this.series.chart.options.series[1].data[this.index][1].toFixed(
                                                this.series.chart.options.additionalData.decimals
                                            ) == 0
                                                ? ''
                                                : this.series.chart.options.series[1].data[this.index][1].toFixed(
                                                      this.series.chart.options.additionalData.decimals
                                                  ) > 0
                                                ? 'S'
                                                : 'P'
                                        }`,
                                    };
                                } else {
                                    point = {
                                        min: parseFloat(
                                            this.series.chart.options.series[1].data[this.index][0]
                                        ).toFixed(this.series.chart.options.additionalData.decimals),
                                        max: parseFloat(
                                            this.series.chart.options.series[1].data[this.index][1]
                                        ).toFixed(this.series.chart.options.additionalData.decimals),
                                        low: parseFloat(this.low).toFixed(
                                            this.series.chart.options.additionalData.decimals
                                        ),
                                        high: parseFloat(this.high).toFixed(
                                            this.series.chart.options.additionalData.decimals
                                        ),
                                        median: parseFloat(this.median).toFixed(
                                            this.series.chart.options.additionalData.decimals
                                        ),
                                        q1: parseFloat(this.q1).toFixed(
                                            this.series.chart.options.additionalData.decimals
                                        ),
                                        q3: parseFloat(this.q3).toFixed(
                                            this.series.chart.options.additionalData.decimals
                                        ),
                                    };
                                }
                                if (!this.series.chart.options.additionalData.reverse) {
                                    return `</br>
                        <div>${this.series.name}</div></br>
                        <div >
                          <div>Max : </div><div>${point.max}</div></br>
                          <div>95% : </div><div>${point.high}</div></br>
                          <div>75% : </div><div>${point.q3}</div></br>
                          <div>Med : </div><div>${point.median}</div></br>
                          <div>25% : </div><div>${point.q1}</div></br>
                          <div>5% : </div><div>${point.low}</div></br>
                          <div>Min : </div><div>${point.min}</div></br>
                        </div>`;
                                }
                                return `</br>
                    <div>${this.series.name}</div></br>
                    <div >
                        <div>Min : </div><div>${point.min}</div></br>
                        <div>5% : </div><div>${point.low}</div></br>
                        <div>25% : </div><div>${point.q1}</div></br>
                        <div>Med : </div><div>${point.median}</div></br>
                        <div>75% : </div><div>${point.q3}</div></br>
                        <div>95% : </div><div>${point.high}</div></br>
                        <div>Max : </div><div>${point.max}</div></br>
                    </div>`;
                            },
                        },
                        point: {
                            events: {
                                click: function (event) {
                                    this.props.getHistogram(event.point.index, {
                                        last: this.state.options.series[1].data.length,
                                        min: parseFloat(this.state.options.series[1].data[event.point.index][0]),
                                        max: parseFloat(this.state.options.series[1].data[event.point.index][1]),
                                        low: event.point.low,
                                        q1: event.point.q1,
                                        median: event.point.median,
                                        q3: event.point.q3,
                                        high: event.point.high,
                                    });
                                }.bind(this),
                            },
                        },
                    },
                    {
                        name: 'Range',
                        data: this.props.data.data.map((x) => [x[0], x[6]]),
                        type: 'arearange',
                        lineWidth: 0,
                        visible: true,
                        linkedTo: ':previous',
                        color: '#dddddd',
                        fillOpacity: 0.5,
                        zIndex: 0,
                        marker: {
                            enabled: false,
                        },
                        enableMouseTracking: false,
                        tooltip: {
                            enabled: false,
                        },
                    },
                ],
            },
        });
    }

    render(): JSX.Element {
        return (
            <div className="relative ">
                <div className="absolute mt-2 z-20 ml-10 flex">
                    <div className="col-span-3 mr-2">
                        <h3 className="text-xs  noselect font-medium text-gray-900">{translate('Min/Max')} </h3>
                    </div>
                    <CheckBox
                        onChange={() => {
                            const { options } = this.state;
                            options.series[1].visible = !options.series[1].visible;

                            this.setState(
                                {
                                    options: options,
                                },
                                () => {
                                    const chart = this.chartRef?.current?.chart;
                                    const series = chart?.series[1];
                                    series?.setVisible(this.state.options?.series[1]?.visible);
                                }
                            );
                        }}
                        value={this.state.options?.series[1]?.visible}
                    ></CheckBox>
                </div>

                <HighchartsReact ref={this.chartRef} highcharts={Highcharts} options={this.state.options} />
            </div>
        );
    }
}

export default ProfileChart;
